import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { listMoments } from "@/data/lifestyle_moments";

/**
 * <MiniStories/> — a 5-tile lifestyle rail (Subscriptions · Documents ·
 * Cards · Together · Business). Poster-still-only, no autoplaying video
 * on the homepage — the rail exists to add human warmth without a
 * performance cost. Every tile is a `<Link>` to the relevant module.
 * Uses IntersectionObserver to lazy-mount the row when it enters the
 * viewport.
 */
export default function MiniStories() {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);
    const items = listMoments("mini-stories");

    useEffect(() => {
        if (!ref.current || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
        const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && setVisible(true)),
            { rootMargin: "150px 0px" });
        io.observe(ref.current);
        return () => io.disconnect();
    }, []);

    if (!items.length) return null;

    return (
        <section ref={ref} className="ms-section" data-testid="mini-stories" aria-label="Real-life mini stories">
            <style>{MS_CSS}</style>
            <div className="ms-container">
                <div className="ms-eyebrow">
                    <Sparkles size={11} strokeWidth={2.2} /> REAL LIFE, ORGANISED
                </div>
                <h2 className="ms-title">Small moments that VAULT quietly fixes.</h2>
                <div className="ms-rail" role="list">
                    {items.map((m) => {
                        // Build deterministic deep-link: route + optional ?view=<key>.
                        const route = m.cta?.route || m.cta?.to || "/";
                        const view  = m.cta?.view;
                        const href  = view ? `${route}?view=${encodeURIComponent(view)}` : route;
                        const onClick = () => {
                            try {
                                if (m.analytics_event && typeof window !== "undefined") {
                                    // eslint-disable-next-line no-undef
                                    (window.gtag || window.dataLayer?.push || (()=>{}))({
                                        event: m.analytics_event,
                                        source: "homepage_small_moments",
                                    });
                                }
                            } catch { /* analytics never blocks navigation */ }
                        };
                        return (
                        <Link
                            key={m.id}
                            to={href}
                            onClick={onClick}
                            className="ms-card"
                            role="listitem"
                            data-testid={`mini-${m.id}`}
                            data-analytics-event={m.analytics_event}
                            aria-label={`${m.headline} — ${m.cta?.label || "Explore"}`}
                        >
                            <div className="ms-media">
                                {visible && (
                                    <img
                                        src={m.poster_desktop}
                                        alt={m.alt_description}
                                        className="ms-poster"
                                        loading="lazy"
                                    />
                                )}
                            </div>
                            <div className="ms-body">
                                <div className="ms-eyebrow ms-eyebrow--card">{(m.eyebrow || "").toUpperCase()}</div>
                                <div className="ms-headline">&ldquo;{m.headline}&rdquo;</div>
                                <div className="ms-link">{m.cta?.label} <ArrowRight size={11} /></div>
                            </div>
                        </Link>);
                    })}
                </div>
            </div>
        </section>
    );
}

const MS_CSS = `
.ms-section { padding: 72px 24px; background: #0F1E3A; color: #F6F3EC; }
.ms-container { max-width: 1160px; margin: 0 auto; }
.ms-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #E6D5A8; margin-bottom: 14px; }
.ms-eyebrow--card { color: #C6A86B; margin-bottom: 8px; }
.ms-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(28px, 4vw, 42px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; margin: 0 0 28px; color: #F6F3EC; max-width: 640px; }
.ms-rail { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
@media (max-width: 1100px) { .ms-rail { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 720px) {
  .ms-rail { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; margin: 0 -24px; padding: 0 24px 4px; scrollbar-width: none; }
  .ms-rail::-webkit-scrollbar { display: none; }
  .ms-card { flex: 0 0 260px; scroll-snap-align: start; }
}
.ms-card { display: flex; flex-direction: column; background: rgba(255,255,255,0.04); border: 1px solid rgba(230,213,168,0.14); border-radius: 16px; overflow: hidden; text-decoration: none; color: inherit; transition: transform .24s cubic-bezier(0.16,1,0.3,1), border-color .24s cubic-bezier(0.16,1,0.3,1), background-color .24s cubic-bezier(0.16,1,0.3,1); }
.ms-card:hover { transform: translateY(-3px); background: rgba(230,213,168,0.08); border-color: rgba(230,213,168,0.35); }
.ms-media { aspect-ratio: 4 / 5; overflow: hidden; background: #0A1424; position: relative; }
.ms-poster { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.86; transition: opacity .24s cubic-bezier(0.16,1,0.3,1); }
.ms-card:hover .ms-poster { opacity: 1; }
.ms-body { padding: 14px 16px 16px; }
.ms-headline { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 18px; line-height: 1.2; letter-spacing: -0.005em; color: #F6F3EC; margin-bottom: 10px; }
.ms-link { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 600; color: #E6D5A8; }
.ms-card:hover .ms-link { color: #F6F3EC; }
`;
